
import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  progressive?: boolean;
}

export interface ResponsiveImageConfig {
  breakpoints: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  qualities: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
}

const DEFAULT_CONFIG: ResponsiveImageConfig = {
  breakpoints: {
    mobile: 480,
    tablet: 768,
    desktop: 1200
  },
  qualities: {
    mobile: 70,
    tablet: 80,
    desktop: 85
  }
};

/**
 * Advanced image compression with multiple quality levels
 */
export const compressImageAdvanced = async (
  file: File,
  options: CompressionOptions = {}
): Promise<File> => {
  const {
    maxSizeMB = 1.5,
    maxWidthOrHeight = 2560,
    useWebWorker = true,
    quality = 0.8,
    format = 'webp',
    progressive = true
  } = options;

  try {
    // First pass - aggressive compression
    const firstPassOptions = {
      maxSizeMB: maxSizeMB * 1.5, // Allow slightly larger for first pass
      maxWidthOrHeight,
      useWebWorker,
      fileType: `image/${format}`,
      initialQuality: quality,
      alwaysKeepResolution: false
    };

    let compressedFile = await imageCompression(file, firstPassOptions);

    // Second pass - fine-tune if still too large
    if (compressedFile.size > maxSizeMB * 1024 * 1024) {
      const secondPassOptions = {
        maxSizeMB,
        maxWidthOrHeight: Math.floor(maxWidthOrHeight * 0.8),
        useWebWorker,
        fileType: `image/${format}`,
        initialQuality: quality * 0.9,
        alwaysKeepResolution: false
      };

      compressedFile = await imageCompression(compressedFile, secondPassOptions);
    }

    console.log(`Advanced compression: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
    
    return compressedFile;
  } catch (error) {
    console.error('Advanced compression failed:', error);
    return file;
  }
};

/**
 * Generate responsive image URLs for different screen sizes
 */
export const generateResponsiveUrls = (
  baseUrl: string,
  config: ResponsiveImageConfig = DEFAULT_CONFIG
): { srcSet: string; sizes: string } => {
  if (!baseUrl || baseUrl === '/placeholder.svg') {
    return { srcSet: baseUrl, sizes: '100vw' };
  }

  try {
    const urlObj = new URL(baseUrl);
    const { breakpoints, qualities } = config;

    // Generate URLs for different screen sizes
    const mobileUrl = addImageParams(urlObj.toString(), breakpoints.mobile, qualities.mobile);
    const tabletUrl = addImageParams(urlObj.toString(), breakpoints.tablet, qualities.tablet);
    const desktopUrl = addImageParams(urlObj.toString(), breakpoints.desktop, qualities.desktop);

    const srcSet = [
      `${mobileUrl} ${breakpoints.mobile}w`,
      `${tabletUrl} ${breakpoints.tablet}w`,
      `${desktopUrl} ${breakpoints.desktop}w`
    ].join(', ');

    const sizes = [
      `(max-width: ${breakpoints.mobile}px) ${breakpoints.mobile}px`,
      `(max-width: ${breakpoints.tablet}px) ${breakpoints.tablet}px`,
      `${breakpoints.desktop}px`
    ].join(', ');

    return { srcSet, sizes };
  } catch (error) {
    console.error('Error generating responsive URLs:', error);
    return { srcSet: baseUrl, sizes: '100vw' };
  }
};

/**
 * Add image optimization parameters to URL
 */
const addImageParams = (url: string, width: number, quality: number): string => {
  try {
    const urlObj = new URL(url);
    
    // For Supabase storage URLs
    if (url.includes('supabase.co/storage')) {
      urlObj.searchParams.set('width', width.toString());
      urlObj.searchParams.set('quality', quality.toString());
      urlObj.searchParams.set('format', 'webp');
      return urlObj.toString();
    }
    
    // For Unsplash URLs
    if (url.includes('unsplash.com')) {
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('q', quality.toString());
      urlObj.searchParams.set('auto', 'format');
      urlObj.searchParams.set('fit', 'crop');
      return urlObj.toString();
    }
    
    return url;
  } catch (error) {
    console.error('Error adding image params:', error);
    return url;
  }
};
